import React, { useState, useEffect } from 'react';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from "react-helmet-async";
import axios from 'axios';
import config from '../../config.json';
import { motion } from "framer-motion";
import { Youtube, ExternalLink, Play, Calendar } from 'lucide-react';

const curatedPlayList = [
  { id: { videoId: "tbB_ZFyk3Ns" }, snippet: { title: "VJ Farewell Ceremony - Vidyajyoti", publishedAt: "2022-03-19" } },
  { id: { videoId: "fZX18rE327A" }, snippet: { title: "Inaugural Mass - Vidyajyoti College New Delhi", publishedAt: "2020-08-10" } },
  { id: { videoId: "BmTR_zL_tZo" }, snippet: { title: "Karwan-e-Mohabbat - Peace Caravan", publishedAt: "2017-09-15" } },
  { id: { videoId: "5CTMn2W7Dkg" }, snippet: { title: "Apostolic Ministry Presentation", publishedAt: "2016-11-20" } },
  { id: { videoId: "4hLz9Y67NU8" }, snippet: { title: "Asha - Hope with Rickshaw Pullers", publishedAt: "2016-04-12" } },
  { id: { videoId: "im9gcaHZqI4" }, snippet: { title: "Balmela Celebration", publishedAt: "2015-12-05" } },
  { id: { videoId: "zZ2Wd9gd-Ug" }, snippet: { title: "Diaconate Ordination Ceremony", publishedAt: "2016-02-28" } },
  { id: { videoId: "44TOjoE9vfg" }, snippet: { title: "Theological Drama Production", publishedAt: "2017-03-10" } },
  { id: { videoId: "39iuJK34MyI" }, snippet: { title: "Together Along the Way - Campus Life", publishedAt: "2018-01-14" } },
  { id: { videoId: "5OkYkS8sIQc" }, snippet: { title: "Diaconate Ordination - Vidyajyoti College", publishedAt: "2017-02-25" } },
  { id: { videoId: "jgZg3NvmvAI" }, snippet: { title: "Stand with Stan Swamy SJ Memorial", publishedAt: "2021-07-10" } },
  { id: { videoId: "9pYPh22hgQk" }, snippet: { title: "Diaconate Ordination Ceremony at Millennium Hall", publishedAt: "2022-02-26" } }
];

export default function YouTubeChannel() {
  const [loaded, setLoaded] = useState(false);
  const [videos, setVideos] = useState([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [activeVideoId, setActiveVideoId] = useState(null);

  const apiKey = config.youtube.apiKey;
  const channelId = config.youtube.channelId;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get('https://www.googleapis.com/youtube/v3/search', {
          params: {
            key: apiKey,
            channelId: channelId,
            part: 'snippet',
            order: 'date',
            type: 'video',
            maxResults: 30,
          },
        });

        if (response.data && response.data.items && response.data.items.length > 0) {
          setVideos(response.data.items);
        } else {
          setVideos(curatedPlayList);
        }
        setLoaded(true);
      } catch (error) {
        console.warn("Using curated fallback YouTube playlist due to API:", error.message);
        setVideos(curatedPlayList);
        setLoaded(true);
      }
    };

    fetchVideos();
  }, [apiKey, channelId]);

  const filteredVideos = videos
    .filter(video => video.id && video.id.videoId)
    .filter(video =>
      filterQuery === '' ||
      (video.snippet?.title || '').toLowerCase().includes(filterQuery.toLowerCase())
    );

  return (
    <>
      <Helmet>
        <title>YouTube Channel & Video Gallery - Vidyajyoti College of Theology</title>
        <meta name="description" content="Watch lectures, inaugural masses, ordinations, conferences, and student events from Vidyajyoti College of Theology." />
        <meta name="keywords" content="Vidyajyoti YouTube, Theology lectures, Catholic videos, Jesuit videos Delhi" />
        <link rel="canonical" href="/publications/youtube" />
      </Helmet>

      <Preloader show={!loaded} />

      <div className="py-5" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        <div className="container">
          {/* Header Banner */}
          <div className="text-center mx-auto pb-4" style={{ maxWidth: '750px' }}>
            <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(255, 0, 0, 0.08)', color: '#cc0000', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(255, 0, 0, 0.2)' }}>
              <Youtube size={16} /> Official Media Channel
            </div>
            <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
              YouTube Video Gallery
            </h1>
            <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
              Explore theological webinars, inaugural masses, diaconate ordinations, student ministries, cultural presentations, and academic lectures.
            </p>

            <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
              <a
                href="https://youtube.com/@vidyajyotiirs"
                target="_blank"
                rel="noopener noreferrer"
                className="btn px-4 py-2.5 rounded-pill fw-semibold text-white d-inline-flex align-items-center gap-2 shadow-sm"
                style={{ background: '#cc0000', border: 'none', transition: 'all 0.3s ease' }}
              >
                <Youtube size={18} /> Subscribe to Channel <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Search Bar */}
          <div className="row justify-content-center mb-5">
            <div className="col-md-6 col-lg-5">
              <div className="input-group bg-white rounded-pill p-1 shadow-sm border">
                <span className="input-group-text bg-transparent border-0 ps-3 text-muted">
                  <Play size={16} />
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-transparent shadow-none"
                  placeholder="Search video titles..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  style={{ fontSize: '0.95rem' }}
                />
                {filterQuery && (
                  <button
                    className="btn btn-sm text-muted border-0 me-2"
                    onClick={() => setFilterQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Video Grid */}
          <div className="row g-4 justify-content-center">
            {filteredVideos.map((video, index) => {
              const videoId = video.id.videoId;
              const thumbnailUrl =
                video.snippet?.thumbnails?.maxres?.url ||
                video.snippet?.thumbnails?.high?.url ||
                video.snippet?.thumbnails?.medium?.url ||
                `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
              const isPlaying = activeVideoId === videoId;

              return (
                <motion.div
                  key={videoId || index}
                  className="col-md-6 col-lg-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.05, 0.4) }}
                >
                  <div
                    className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white"
                    style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div className="ratio ratio-16x9 bg-dark rounded-top overflow-hidden">
                      {isPlaying ? (
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
                          title={video.snippet?.title || 'YouTube video'}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="w-100 h-100 border-0"
                        />
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                          onClick={() => setActiveVideoId(videoId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setActiveVideoId(videoId);
                            }
                          }}
                          title="Click to play video"
                        >
                          <img
                            src={thumbnailUrl}
                            alt={video.snippet?.title || 'Video thumbnail'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
                            }}
                          />
                          {/* Play overlay banner */}
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                            style={{
                              background: 'rgba(0, 0, 0, 0.22)',
                              transition: 'background 0.3s ease'
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle shadow-lg"
                              style={{
                                width: '56px',
                                height: '56px',
                                background: 'rgba(204, 0, 0, 0.95)',
                                color: '#fff',
                                transition: 'transform 0.2s ease'
                              }}
                            >
                              <Play size={22} className="ms-1" fill="#fff" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="card-body p-3 d-flex flex-column justify-content-between">
                      <h6 className="fw-bold mb-2 text-dark" style={{ lineHeight: '1.4', fontSize: '0.98rem' }}>
                        {video.snippet?.title || 'Vidyajyoti College Video'}
                      </h6>

                      <div className="d-flex align-items-center justify-content-between text-muted small pt-2 border-top">
                        <span className="d-flex align-items-center gap-1">
                          <Calendar size={13} />
                          {video.snippet?.publishedAt ? new Date(video.snippet.publishedAt).toLocaleDateString() : 'Vidyajyoti IRS'}
                        </span>
                        <a
                          href={`https://www.youtube.com/watch?v=${videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none fw-semibold"
                          style={{ color: '#79031d', fontSize: '0.8rem' }}
                        >
                          Watch on YouTube ↗
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
